/**
 * Help & Support Layout Tab — superuser-only. Toggles which FAQ categories
 * and sidebar cards appear on /help for this tenant. Customer admins cannot
 * edit this; a guard check in the tab, plus a 403 from the backend when a
 * non-superuser PATCHes help_layout, is the defence in depth.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { LifeBuoy, Lock } from 'lucide-react';
import { platformSettingsAPI, type HelpLayout } from '../../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';

const FAQ_FIELDS: Array<{ id: keyof HelpLayout['faq']; label: string; description: string }> = [
  { id: 'getting_started', label: 'Getting Started', description: 'Navigation basics, profile and password.' },
  { id: 'onboarding', label: 'Onboarding', description: 'Invite / approve / renew suppliers (customer side) and complete-and-submit (supplier side).' },
  { id: 'questionnaires', label: 'Questionnaires', description: 'Filling + carry-forward for suppliers; assigning + templates for customers.' },
  { id: 'purchasing', label: 'Purchasing', description: 'Purchase orders, acknowledgements, propose-change flow. Hide for compliance-only tenants.' },
  { id: 'supplier_management', label: 'Supplier Management', description: 'Performance metrics and qualification process. Hide if your tenant doesn\'t track OTIF/NCRs.' },
  { id: 'documents_communication', label: 'Documents & Communication', description: 'Uploads, supplier-visible docs, news announcements.' },
];

const SIDEBAR_FIELDS: Array<{ id: keyof HelpLayout['sidebar']; label: string; description: string }> = [
  { id: 'tour', label: 'New to Helix? (Tour)', description: 'Interactive tour + Quick Reference button.' },
  { id: 'contact_support', label: 'Contact Support card', description: 'Email and phone support block.' },
  { id: 'system_status', label: 'System Status card', description: 'Green-light system status indicator.' },
];

export function HelpLayoutTab() {
  const { user } = useAuth();
  const { helpLayout, updateHelpLayout } = useMenuSettings();
  const [layout, setLayout] = useState<HelpLayout>(helpLayout);
  const [saving, setSaving] = useState(false);
  const isSuperuser = Boolean(user?.is_superuser);

  useEffect(() => { setLayout(helpLayout); }, [helpLayout]);

  const setFaq = (id: keyof HelpLayout['faq'], val: boolean) =>
    setLayout(prev => ({ ...prev, faq: { ...prev.faq, [id]: val } }));
  const setSidebar = (id: keyof HelpLayout['sidebar'], val: boolean) =>
    setLayout(prev => ({ ...prev, sidebar: { ...prev.sidebar, [id]: val } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ help_layout: layout } as unknown as Record<string, unknown>);
      updateHelpLayout(layout);
      toast.success('Help & Support layout saved');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) toast.error('Superuser access is required to change the Help layout.');
      else toast.error('Failed to save Help & Support layout');
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (def: { id: string; label: string; description: string }, checked: boolean, onChange: (v: boolean) => void) => (
    <div key={def.id} className="flex items-center justify-between gap-3 p-3 border rounded-lg">
      <div className="flex-1">
        <Label className="text-sm font-medium">{def.label}</Label>
        <p className="text-xs text-muted-foreground mt-0.5">{def.description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={!isSuperuser} />
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5" /> Help & Support Layout
          </CardTitle>
          <CardDescription>
            Hide FAQ categories and sidebar cards that aren't relevant for this
            tenant (for example, compliance-only tenants that don't run
            purchasing). Search, legal links and header are always visible.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!isSuperuser && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground border rounded-md p-3 bg-muted/40">
              <Lock className="h-4 w-4" />
              Read-only — only a superuser can change the Help & Support layout.
            </div>
          )}

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase">FAQ Categories</h3>
            {FAQ_FIELDS.map(f => renderRow(
              { id: String(f.id), label: f.label, description: f.description },
              layout.faq[f.id] ?? true,
              v => setFaq(f.id, v),
            ))}
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase">Sidebar Cards</h3>
            {SIDEBAR_FIELDS.map(f => renderRow(
              { id: String(f.id), label: f.label, description: f.description },
              layout.sidebar[f.id] ?? true,
              v => setSidebar(f.id, v),
            ))}
          </section>

          <Button className="w-full" disabled={saving || !isSuperuser} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save Layout'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
